<template src="./template.html"></template>
<script>
import BreadCrumbs from '@/components/BreadCrumbs/index.vue'
import SingleCheckbox from '@/components/Form/Checkbox/SingleCheckbox'
import LoadingSpinner from '@/components/LoadingSpinner/index.vue'
import DriverNewForm from '@/components/RouteTemplates/Form.vue'
import SectionHeader from '@/components/SectionHeader'
import BinitySlider from '@/components/Slider.vue'
import CompanyNav from '@/components/company_nav'
import stringUtils from '@/components/mixins/stringUtils'

import companyUsersApi from '@/api/companyUsers'
import locationsApi from '@/api/locations'
import projectsApi from '@/api/projects'
import routeTemplatesApi from '@/api/routeTemplates'
import vehiclesApi from '@/api/vehicles'

export default {
  name: 'route_templates',
  mixins: [stringUtils],
  inject: ['companyUser', 'showPopup'],
  data() {
    return {
      collectionModes: {
        containerLevel: 'container_level',
        pointLevel: 'collection_point_level',
      },
      companyUsers: [],
      deleteOption: false,
      drivers: [],
      includeStaticContainers: false,
      includeContainerGroupAtStop: false,
      loading: true,
      locations: [],
      noTemplateTypeError: false,
      projects: [],
      routeTemplate: {
        company_uuid: '',
        vehicles: [],
        users: [],
        fair_distribution: null,
        locality_clustering: null,
        profitability: null,
        short_paths: null,
        worker_reduction: null,
        minimal_collect_fill_level: null,
      },
      presets: [
        {
          value: 1,
          name: this.$t('routes.templatePresetEcoFriendly'),
          values: {
            fair_distribution: 0,
            locality_clustering: 0,
            profitability: 9,
            short_paths: 9,
            worker_reduction: 0,
          },
        },
        {
          value: 2,
          name: this.$t('routes.templatePresetWorkerOriented'),
          values: {
            fair_distribution: 9,
            locality_clustering: 0,
            profitability: 4,
            short_paths: 5,
            worker_reduction: 0,
          },
        },
        {
          value: 3,
          name: this.$t('routes.templatePresetCustomerOriented'),
          values: {
            fair_distribution: 0,
            locality_clustering: 0,
            profitability: 2,
            short_paths: 2,
            worker_reduction: 0,
          },
        },

        {
          value: 4,
          name: this.$t('routes.templatePresetFleetOriented'),
          values: {
            fair_distribution: 0,
            locality_clustering: 0,
            profitability: 2,
            short_paths: 2,
            worker_reduction: 9,
          },
        },
      ],
      routePreset: 0,
      selectedStartPoint: null,
      selectedEndPoint: null,
      selectedCollectionMode: null,
      templateTypes: {
        static: 'static',
        adiutabyte: 'adiutabyte_dynamic',
      },
      templateMode: 'add',
      vehicles: [],
    }
  },
  computed: {
    formattedAssumedFillLevelStatic: {
      get() {
        return this.includeStaticContainers && this.routeTemplate.assumed_fill_level_static
          ? Math.round(this.routeTemplate.assumed_fill_level_static * 100)
          : 0
      },
      set(newValue) {
        this.routeTemplate.assumed_fill_level_static = newValue / 100
      },
    },
    formattedMinCollectFillLevel: {
      get() {
        return this.routeTemplate.minimal_collect_fill_level
          ? Math.round(this.routeTemplate.minimal_collect_fill_level * 100)
          : 0
      },
      set(newValue) {
        this.routeTemplate.minimal_collect_fill_level = newValue / 100
      },
    },
    locationOptions() {
      return this.locations.map(location => ({
        location: {
          name: location.name,
          address: location.preformatted,
        },
        coordinates: {
          lat: location.lat,
          lng: location.lng,
        },
      }))
    },
  },
  components: {
    BinitySlider,
    BreadCrumbs,
    CompanyNav,
    DriverNewForm,
    LoadingSpinner,
    SectionHeader,
    SingleCheckbox,
  },
  methods: {
    async getData() {
      ;[this.vehicles, this.projects, this.locations, this.companyUsers] = await Promise.all([
        vehiclesApi.selectableVehicles(this.companyUser.company_uuid),
        projectsApi.getAll(this.companyUser.company_uuid),
        locationsApi.getAllForCompany(this.companyUser.company_uuid),
        companyUsersApi.getWithPagination({ company_uuid: this.companyUser.company_uuid }),
      ])

      this.companyUsers = this.companyUsers.filter(user => user.user.email !== 'default-driver@example.com')

      this.routeTemplate.company_uuid = this.companyUser.company_uuid
      this.selectedCollectionMode = this.collectionModes.containerLevel
      this.loading = false
    },
    setCustomPreset() {
      this.routePreset = 5
    },
    setPreset(event) {
      var selectedPreset = this.presets.find(x => x.value == event.target.value)
      this.routeTemplate = {
        ...this.routeTemplate,
        ...selectedPreset.values,
      }
    },
    setStartPoint() {
      this.routeTemplate.start_lat = this.selectedStartPoint?.lat
      this.routeTemplate.start_lng = this.selectedStartPoint?.lng
    },
    setEndPoint() {
      this.routeTemplate.end_lat = this.selectedEndPoint?.lat
      this.routeTemplate.end_lng = this.selectedEndPoint?.lng
    },
    setCollectionMode() {
      this.routeTemplate.collection_mode = this.selectedCollectionMode
    },
    async evaluateTemplate() {
      // Evaluate route template
      const result = await routeTemplatesApi.evaluate({
        company_uuid: this.routeTemplate.company_uuid,
        project_uuid: this.routeTemplate.project_uuid,
        vehicles: this.drivers.map(driver => driver.vehicle.value),
      })
      if (result.fraction_count == 0) {
        const fractionAlert = await this.showPopup({
          content: this.$t('routes.fractionZeroAlert'),
          type: 'confirm',
        })
        if (!fractionAlert) {
          return false
        }
      }

      if (result.matching_container_count == 0) {
        const containerCountAlert = await this.showPopup({
          content: this.$t('routes.containerZeroAlert'),
          type: 'confirm',
        })
        if (!containerCountAlert) {
          return false
        }
      }
      return true
    },
    async onFormSubmitted() {
      this.noTemplateTypeError = !this.routeTemplate.template_type
      if (this.noTemplateTypeError) return

      const evaluateResponse = await this.evaluateTemplate()
      if (!evaluateResponse) return

      this.drivers.map(driver => {
        this.routeTemplate.vehicles.push(driver.vehicle.value)
        this.routeTemplate.users.push(driver.user.uuid)
      })

      this.routeTemplate.collection_mode = this.includeContainerGroupAtStop
        ? this.collectionModes.pointLevel
        : this.collectionModes.containerLevel

      // create route template
      const response = await routeTemplatesApi.create(this.routeTemplate)

      if (response.success) {
        await this.$router.push('/company/routes')
      }
    },
    setTemplateTypeDefaults() {
      if (this.routeTemplate.template_type === this.templateTypes.static) {
        this.routeTemplate.fair_distribution = null
        this.routeTemplate.locality_clustering = null
        this.routeTemplate.profitability = null
        this.routeTemplate.short_paths = null
        this.routeTemplate.worker_reduction = null

        this.routeTemplate.minimal_collect_fill_level = null
      } else if (this.routeTemplate.template_type === this.templateTypes.adiutabyte) {
        this.routeTemplate.fair_distribution = 0
        this.routeTemplate.locality_clustering = 0
        this.routeTemplate.profitability = 0
        this.routeTemplate.short_paths = 0
        this.routeTemplate.worker_reduction = 0

        this.routeTemplate.minimal_collect_fill_level = 0.5
      }
    },
  },
  watch: {
    includeStaticContainers(val) {
      // When checkbox is toggled, we update the assumed fill level static value.
      // The default value for assumed fill level static is 0.7, i.e. 70%.
      // Resetting the value to 0 when it the checkbox is unchecked tells the backend that
      // the static containers should not be included in the route.
      val ? (this.routeTemplate.assumed_fill_level_static = 0.7) : (this.routeTemplate.assumed_fill_level_static = 0)
    },
    selectedStartPoint() {
      this.setStartPoint()
    },
    selectedEndPoint() {
      this.setEndPoint()
    },
    selectedCollectionMode() {
      this.setCollectionMode()
    },
    routeTemplate: {
      async handler(newValue) {
        if (newValue.template_type) this.noTemplateTypeError = false
      },
      deep: true,
    },
  },
  mounted() {
    this.getData()
  },
}
</script>

<style lang="scss" src="./style.scss" scoped />
